import { useState } from "react";

import { apiResponseMiddleware } from "@/middleware/api-response";
import axios from "axios";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";

import BookDetail from "./BookDetail";

const API_URL = import.meta.env.VITE_API_URL as string;
const API_ENDPOINT_BIBLIOGRAPHY = "book/GetBookByBibliography";
const API_ENDPOINT_ISBN = "book/GetBookByISBN";

const NewBook = () => {
  const [option, setOption] = useState<"bibliography" | "isbn">("bibliography");
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  type BookData = Record<string, string | string[]>;
  const [bookData, setBookData] = useState<BookData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const handleSearch = () => {
    if (inputValue.trim() === "") {
      toast.error("لطفا شماره کتابشناسی ملی یا ISBN را وارد کنید", {
        id: "input-error"
      });
      return;
    }

    setLoading(true);
    setError(null);
    setBookData(null);

    const endpoint =
      option === "bibliography" ? API_ENDPOINT_BIBLIOGRAPHY : API_ENDPOINT_ISBN;
    const paramKey =
      option === "bibliography" ? "NationalBibliographyNumber" : "ISBN";

    const toastId = toast.loading("در حال دریافت اطلاعات کتاب...", {
      id: "loading-toast"
    });

    apiResponseMiddleware(
      axios.get(`${API_URL}/${endpoint}`, {
        params: { [paramKey]: inputValue.trim() }
      }),
      ({ data }: any) => {
        toast.dismiss(toastId);

        if (!data) {
          setLoading(false);
          setBookData(null);
          setError("کتابی با این مشخصات یافت نشد.");
          toast.error("کتابی با این مشخصات یافت نشد", { id: "no-book-found" });
          return;
        }

        const formattedData: BookData = {
          "شماره کتاب": data.id || "",
          "شناسه رکورد": data.recordIdentifier || "",
          "شابک (ISBN)": data.isbn || "",
          "شماره کتابشناسی ملی": data.nationalBibliographyNumbers || "",
          "عنوان کتاب": data.mainTitleFa || data.mainTitleEn || "",
          "عنوان فرعی": data.subTitleFa || data.subTitleEn || "",
          "عناوین جایگزین":
            data.alternativeTitlesFa || data.alternativeTitlesEn || "",
          ویرایش: data.editionsFa || data.editionsEn || "",
          "ویرایش اول": data.firstEditorsFa || data.firstEditorsEn || "",
          "محل نشر":
            data.placeOfPublicationFa || data.placeOfPublicationEn || "",
          ناشر: data.publisherFa || data.publisherEn || "",
          "تاریخ نشر": data.publicationDate || "",
          ...Object.fromEntries(
            data.notesFa
              ? data.notesFa
                  .split("|")
                  .map((note: string, index: number) => [
                    `یادداشت ${index + 1}`,
                    note.trim()
                  ])
              : []
          ),
          "زبان عناوین جایگزین":
            data.alternativeLanguageTitlesFa ||
            data.alternativeLanguageTitlesEn ||
            "",
          "سایر نویسندگان همکار":
            data.bookCoAuthorsRelation
              ?.map((author: any) => {
                const role = author.authorRoleFa
                  ? ` (${author.authorRoleFa})`
                  : "";
                return `${author.bookCoAuthor.fullName}${role}`;
              })
              ?.join(" | ") || "",
          "یادداشت نویسنده": data.authorNoteFa || data.authorNoteEn || "",
          زبان: data.languages || "",
          "تعداد صفحات":
            data.physicalDescriptionFa || data.physicalDescriptionEn || "",
          "ویژگی فیزیکی":
            [data.bookSize, data.illustrationSize, data.accompanyingMaterialFa]
              .filter(Boolean)
              .join(" - ") || ""
        };

        setBookData(formattedData);
        setLoading(false);
        toast.success("داده کتاب دریافت شد", { id: "search-success" });
      },
      {
        showToast: false
      }
    ).catch(() => {
      setLoading(false);
      setBookData(null);
      toast.dismiss(toastId);
    });
  };

  return (
    <div className="font-sans" dir="rtl">
      <div className="m-10">
        <p className="mb-10 text-right text-gray-700">
          درخواست ثبت کتاب به صورت خودکار از کتابخانه ملی ایران
        </p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <select
              id="options"
              name="options"
              className="h-10 w-[180px] rounded-lg border border-[#E4EBF3] bg-white p-2 text-sm text-gray-700"
              value={option}
              onChange={(e) =>
                setOption(e.target.value as "bibliography" | "isbn")
              }
            >
              <option value="bibliography">شماره کتابشناسی ملی</option>
              <option value="isbn">ISBN</option>
            </select>
          </div>
          <input
            className="h-10 w-[420px] rounded-lg border border-[#E4EBF3] p-2 shadow-md focus:outline-none focus:ring-2 focus:ring-[#0099A5]"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={
              option === "bibliography"
                ? "شماره کتابشناسی ملی را وارد کنید"
                : "ISBN را وارد کنید"
            }
          />
          <Button
            className="h-10 w-[120px] bg-[#0099A5] text-sm text-white disabled:opacity-50"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? "در حال جستجو..." : "جستجو"}
          </Button>
        </div>
        {error && <p className="mt-4 text-right text-red-500">{error}</p>}
        {bookData && (
          <div className="mt-6">
            <BookDetail bookData={bookData} />
          </div>
        )}
      </div>
    </div>
  );
};

export default NewBook;
